import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";

// Constants
export const TREDOT_BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
export const API_PREFIX = "/api/v1";

// Types
export interface TokenPayload {
  id: string | number;
  email: string;
  role: string | undefined;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  accessToken?: string;
  refreshToken?: string;
}

export interface AuthResponse {
  user: User;
  token?: string;
  message?: string;
}

export interface APIError {
  errors?: { path: string; msg: string }[];
  error?: string;
  message?: string;
}

// AuthService Class
export class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private readonly AUTH_STORAGE_KEY = "tredot_auth_data";
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = axios.create({
      baseURL: `${TREDOT_BACKEND_URL}${API_PREFIX}`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
  }

  async getAccessToken(): Promise<string | null> {
    if (this.accessToken) {
      return this.accessToken;
    }
    this.accessToken = await this.retrieveAccessToken();
    return this.accessToken;
  }

  async getRefreshToken(): Promise<string | null> {
    if (this.refreshToken) {
      return this.refreshToken;
    }
    this.refreshToken = await this.retrieveRefreshToken();
    return this.refreshToken;
  }

  async setTokens(tokens: Tokens): Promise<void> {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;

    // Store tokens in localStorage
    const authData = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    };
    localStorage.setItem(this.AUTH_STORAGE_KEY, JSON.stringify(authData));
  }

  async refreshAccessToken(): Promise<string | null> {
    try {
      // Call the refresh endpoint
      const response = await this.apiClient.post(
        "/auth/refresh-token",
        {},
        {
          headers: {
            "X-Retry": "true", // Custom header to mark this request to prevent infinite loops
          },
        }
      );

      // Check for the token in the response
      if (response.data && response.data.token) {
        const newToken = response.data.token;
        // Update stored tokens
        await this.setTokens({
          accessToken: newToken,
          refreshToken: (await this.getRefreshToken()) || "",
        });

        // Show success toast for token refresh
        toast.success("Session refreshed successfully", {
          duration: 2000,
        });

        return newToken;
      }
      return null;
    } catch (error) {
      console.error("Error refreshing token:", error);
      toast.error("Session expired. Please login again.", {
        duration: 4000,
        action: {
          label: "Login",
          onClick: () => window.location.href = "/login"
        }
      });
      await this.logout();
      return null;
    }
  }

  async logout(): Promise<void> {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem(this.AUTH_STORAGE_KEY);

    toast.info("You have been logged out", {
      duration: 3000,
    });
  }

  private async retrieveAccessToken(): Promise<string | null> {
    const authData = localStorage.getItem(this.AUTH_STORAGE_KEY);
    return authData ? JSON.parse(authData).accessToken : null;
  }

  private async retrieveRefreshToken(): Promise<string | null> {
    const authData = localStorage.getItem(this.AUTH_STORAGE_KEY);
    return authData ? JSON.parse(authData).refreshToken : null;
  }
}

// ApiService Class
class ApiService {
  public apiClient: AxiosInstance;
  public authService: AuthService;

  constructor() {
    this.authService = new AuthService();

    this.apiClient = axios.create({
      baseURL: `${TREDOT_BACKEND_URL}${API_PREFIX}`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    this.initializeRequestInterceptor();
    this.initializeResponseInterceptor();
  }

  private initializeRequestInterceptor() {
    this.apiClient.interceptors.request.use(this.requestInterceptor, (error) =>
      Promise.reject(error)
    );
  }

  private requestInterceptor = async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    const token = await this.authService.getAccessToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  };

  private initializeResponseInterceptor() {
    this.apiClient.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 403 &&
          !originalRequest.headers["X-Retry"]
        ) {
          originalRequest.headers["X-Retry"] = "true";
        }
        if (error.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;
          const newAccessToken = await this.authService.refreshAccessToken();
          if (newAccessToken) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return this.apiClient(originalRequest);
          } else {
            // Logout if refresh token is invalid
            await this.authService.logout();
            // Redirect to login page
            window.location.href = "/login";
          }
        }

        // Handle error responses
        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }

  public handleApiError(error: any): string[] {
    const errorMessages: string[] = [];

    if (error.response?.data) {
      const apiError: APIError =
        error.response.data.error?.message || error.response.data;

      if (apiError.errors && Array.isArray(apiError.errors)) {
        // Group multiple validation errors into one toast
        const validationErrors = apiError.errors.map(e => `${e.path}: ${e.msg}`);

        toast.error("Validation Error", {
          description: validationErrors.join(", "),
          id: validationErrors.join(","),
          duration: 5000,
          action: {
            label: "Dismiss",
            onClick: () => { }
          }
        });

        errorMessages.push(...validationErrors);
      } else if (apiError.error) {
        toast.error("Request Failed", {
          id: apiError.error,
          description: apiError.error,
          duration: 4000,
        });
        errorMessages.push(apiError.error);
      } else if (apiError.message) {
        toast.error("Error", {
          id: apiError.message,
          description: apiError.message,
          duration: 4000,
        });
        errorMessages.push(apiError.message);
      }
    } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
      toast.error("Network Error", {
        id: "network-error",
        description: "Please check your internet connection and try again.",
        duration: 5000,
        action: {
          label: "Retry",
          onClick: () => window.location.reload()
        }
      });
      errorMessages.push("Network connection error");
    } else {
      const message = error.message || "An unexpected error occurred";
      toast.error("Something went wrong", {
        id: "unexpected-error",
        description: message,
        duration: 4000,
      });
      errorMessages.push(message);
    }

    return errorMessages;
  }

  // Success helper for API operations
  public showSuccessToast(message: string, description?: string): void {
    toast.success(message, {
      description,
      duration: 3000,
    });
  }

  // Info helper for API operations
  public showInfoToast(message: string, description?: string): void {
    toast.info(message, {
      description,
      duration: 3000,
    });
  }

  // Warning helper for API operations
  public showWarningToast(message: string, description?: string): void {
    toast.info(message, {
      description,
      duration: 4000,
    });
  }

  // Loading toast for long operations
  public showLoadingToast(message: string): string | number {
    return toast.loading(message);
  }

  // Dismiss specific toast
  public dismissToast(toastId: string | number): void {
    toast.dismiss(toastId);
  }

  // Generic API methods with enhanced feedback
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.get<T>(url, config);
    return response.data;
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.apiClient.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.apiClient.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.apiClient.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.delete<T>(url, config);
    return response.data;
  }

  // Helper utilities
  validateEmail(email: string): boolean {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  }
}

// Export a singleton instance of ApiService
const apiService = new ApiService();
export default apiService;